import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { STORY } from '@/data/content';
import { getLenis } from '@/lib/scroll';

function ChapterVisual({ visual }) {
  if (visual === 'city') {
    return (
      <img
        src="/assets/arctoons-web-background-opt.webp"
        alt="Toon City appearing above the clouds"
        loading="lazy"
        className="max-h-[38vh] w-full rounded-2xl border border-electric/25 object-cover shadow-[0_0_50px_rgba(0,200,255,0.2)]"
      />
    );
  }
  if (visual === 'banner') {
    return (
      <img
        src="/assets/arctoons-banner.webp"
        alt="ARC TOONS banner over Toon City"
        loading="lazy"
        className="w-full rounded-2xl border border-electric/25 object-cover shadow-[0_0_50px_rgba(0,200,255,0.2)]"
      />
    );
  }
  if (visual === 'group') {
    return (
      <img
        src="/assets/arctoons-assets-opt.webp"
        alt="The Toons together — fox, frog, cat, robot and whale overlooking Toon City"
        loading="lazy"
        className="max-h-[42vh] w-auto rounded-2xl border border-gold/30 object-contain shadow-[0_0_50px_rgba(255,181,46,0.2)]"
      />
    );
  }
  if (visual === 'trio') {
    return (
      <div className="flex items-end justify-center gap-2 sm:gap-4">
        {['karakter-2', 'karakter-3', 'karakter-4'].map((k, i) => (
          <motion.img
            key={k}
            src={`/assets/${k}-opt.webp`}
            alt={i === 1 ? 'The Builder Toon' : i === 0 ? 'The Strategist Toon' : 'The Agent Toon'}
            loading="lazy"
            className="w-[26%] max-w-[150px] object-contain"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3.4 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>
    );
  }
  return (
    <motion.img
      src="/assets/arctoons-logo-opt.webp"
      alt="ARC TOONS logo"
      loading="lazy"
      className="mx-auto w-44 object-contain sm:w-56"
      style={{ filter: 'drop-shadow(0 0 46px rgba(0,200,255,0.5))' }}
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

function ChapterPanel({ data, idx, active }) {
  return (
    <div
      className={`flex h-full w-full shrink-0 items-center transition-opacity duration-500 sm:w-screen ${
        active ? 'opacity-100' : 'opacity-100'
      }`}
      data-testid={`story-chapter-${data.chapter}`}
    >
      <div className="mx-auto grid w-full max-w-[1300px] items-center gap-8 px-8 sm:px-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div>
          <span
            aria-hidden="true"
            className="text-stroke font-display block text-[24vw] font-extrabold leading-none lg:text-[11rem]"
          >
            {data.chapter}
          </span>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.4em] text-gold">
            CHAPTER {data.chapter} / 05
          </p>
          <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-snow sm:text-4xl">
            {data.title}
          </h3>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-snow/70 sm:text-base">{data.text}</p>
        </div>
        <div className="flex items-center justify-center">
          <ChapterVisual visual={data.visual} />
        </div>
      </div>
    </div>
  );
}

export default function StoryTimeline() {
  const ref = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const x = useTransform(scrollYProgress, [0, 1], ['0vw', '-400vw']);
  const barScale = useTransform(scrollYProgress, [0, 1], [0.02, 1]);

  useEffect(() => {
    if (!isDesktop) return;
    const unsub = scrollYProgress.on('change', (v) => {
      setActive(Math.min(4, Math.floor(v * 5)));
    });
    return unsub;
  }, [scrollYProgress, isDesktop]);

  const jumpTo = (i) => {
    const lenis = getLenis();
    const el = ref.current;
    if (!el) return;
    const top = el.offsetTop;
    const scroll = top + (i / 5) * (el.offsetHeight - window.innerHeight);
    if (lenis) lenis.scrollTo(scroll, { duration: 1.6 });
    else window.scrollTo({ top: scroll, behavior: 'smooth' });
  };

  return (
    <section id="story" data-testid="story-section" ref={ref} className="relative" aria-label="The story begins">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'linear-gradient(180deg, #050B18 0%, #08152B 30%, #08152B 70%, #050B18 100%)' }}
      />
      {isDesktop ? (
        <div className="relative h-[420vh]">
          <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden" data-testid="story-timeline-desktop">
            <div className="pt-20 text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">03 — THE STORY</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
                THE STORY{' '}
                <span className="bg-gradient-to-r from-arc to-electric bg-clip-text text-transparent">BEGINS</span>
              </h2>
            </div>
            <motion.div className="flex" style={{ x }}>
              {STORY.map((data, i) => (
                <ChapterPanel key={data.chapter} data={data} idx={i} active={active === i} />
              ))}
            </motion.div>
            <div className="mx-auto mb-10 w-full max-w-[900px] px-14">
              <div className="h-[3px] w-full overflow-hidden rounded-full bg-electric/15">
                <motion.div
                  className="h-full origin-left rounded-full bg-gradient-to-r from-arc via-electric to-gold"
                  style={{ scaleX: barScale, width: '100%' }}
                  data-testid="story-progress-bar"
                />
              </div>
              <div className="mt-4 flex justify-between">
                {STORY.map((s, i) => (
                  <button
                    key={s.chapter}
                    data-testid={`story-jump-${s.chapter}`}
                    onClick={() => jumpTo(i)}
                    className={`font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                      active === i ? 'text-gold' : 'text-snow/35 hover:text-snow/70'
                    }`}
                  >
                    {s.chapter} {s.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative py-24 sm:py-32" data-testid="story-timeline-mobile">
          <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">03 — THE STORY</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
              THE STORY{' '}
              <span className="bg-gradient-to-r from-arc to-electric bg-clip-text text-transparent">BEGINS</span>
            </h2>
            <div className="mt-14 flex flex-col gap-20">
              {STORY.map((data, i) => (
                <motion.div
                  key={data.chapter}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="grid gap-6"
                >
                  <span
                    aria-hidden="true"
                    className="text-stroke font-display text-7xl font-extrabold leading-none"
                  >
                    {data.chapter}
                  </span>
                  <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-gold">
                    CHAPTER {data.chapter} / 05
                  </p>
                  <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight">{data.title}</h3>
                  <p className="max-w-md text-sm leading-relaxed text-snow/70">{data.text}</p>
                  <ChapterVisual visual={data.visual} />
                  {i < STORY.length - 1 && (
                    <span aria-hidden="true" className="mx-auto h-10 w-px bg-gradient-to-b from-electric to-transparent" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
